import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { Dialog, DialogContent, DialogActions } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBackOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForwardOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InfoIcon from "@mui/icons-material/Info";
import ModalHeader from "../../../../../../components/Modals/ModalControls/ModalHeader";
import ModalAccordion from "../../../../../../components/Modals/ModalAccordion";
import {
  customerGeneralInfo,
  customerAddressInfo,
  customerComment,
} from "../../customerFormDataConfig";
import AlertSnackbar from "../../../../../../components/Notifications/AlertSnackbar";
import { selectNavHeight } from "../../../../../../redux/navbar/navHeightSlice";
import {
  selectedSelectedCustomer,
  setSelectedCustomer,
} from "../../../../../../redux/customers/selectedCustomerSlice";
import {
  selectCurrentPage,
  selectRowsPerPage,
} from "../../../../../../redux/customers/customerPaginationSlice";
import useDebounce from "../../../../../../hooks/useDebounce";
import useCustomerDelete from "../../hooks/useCustomerDelete";
import useCustomerUpdate from "../../hooks/useCustomerUpdate";
import CustomerContactTable from "./Contacts/CustomerContactTable";
import { clearSelectedCustomerContact } from "../../../../../../redux/customers/selectedCustomerContactSlice";
import CustomerBuildingsTable from "./Buildings/CustomerBuildingsTable";
import LoadingCircle from "../../../../../../components/LoadingCircle";
import InformationDisplay from "./InformationDisplay";
import RenderFieldsCustomers from "./RenderFieldsCustomers";
import RenderAddressFields from "../../../../../../components/Modals/ModalFields/RenderAddressFields";
import CreateAccount from "./Account/CreateAccount";

const shallowEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;
  if (!obj1 || !obj2) return false;
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;
  for (let key of keys1) {
    if (obj1[key] !== obj2[key]) return false;
  }
  return true;
};

const CustomerViewModal = ({
  open,
  handleClose,
  handleOpenAddFromView,
  updateList,
  isEditMode,
  setIsEditMode,
  dropdownLists,
}) => {
  const dispatch = useDispatch();
  const navHeight = useSelector(selectNavHeight);
  const selectedCustomer = useSelector(selectedSelectedCustomer);
  const customers = useSelector((state) => state.customers.customers);
  const currentPage = useSelector(selectCurrentPage);
  const rowsPerPage = useSelector(selectRowsPerPage);

  // Use the customer update hook to manage form data and autosave.
  const {
    formData,
    setFormData,
    validationErrors,
    isSubmitting,
    handleUpdateCustomer,
    setValidationErrors,
  } = useCustomerUpdate(updateList, {
    currentPage,
    rowsPerPage,
    searchQuery: "",
  });

  // Local state for edit mode and autosave status.
  const [autosaveStatus, setAutosaveStatus] = useState("");
  const deleteSelected = useCustomerDelete();

  // State for expanded modal view and toggling the additional info panel.
  const [isExpanded, setIsExpanded] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const handleInfoClick = () => setIsInfoOpen((prev) => !prev);

  const [newBuildingId, setNewBuildingId] = useState(null);

  const handleToggleEdit = () => {
    setIsEditMode((prev) => !prev);
    setValidationErrors({});
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
  };

  // Snackbar state and handler for notifications.
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSave = async (autoSave = false) => {
    const success = await handleUpdateCustomer(autoSave);
    if (success && !autoSave) {
      setIsEditMode(false);
    }
    return success;
  };

  useEffect(() => {
    if (!shallowEqual(selectedCustomer, formData)) {
      setFormData(selectedCustomer);
    }
  }, [selectedCustomer]);

  const debouncedFormData = useDebounce(formData, 1500);
  useEffect(() => {
    if (isEditMode) {
      setAutosaveStatus("Saving...");
      handleSave(true).then((success) => {
        if (success) {
          setAutosaveStatus("Saved");
          setTimeout(() => setAutosaveStatus(""), 2000);
        } else {
          setAutosaveStatus("");
        }
      });
    }
  }, [debouncedFormData]);

  const clearSelected = useCallback(() => {
    dispatch(clearSelectedCustomerContact());
  }, [dispatch]);

  const handleDelete = async () => {
    try {
      await deleteSelected();
      setSnackbar({
        open: true,
        message: "Customer deleted successfully.",
        severity: "success",
      });
      dispatch(setSelectedCustomer({}));
      setValidationErrors({});
      setIsEditMode(false);
      handleClose();
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: "error" });
    }
  };

  const handlePrev = useCallback(() => {
    if (!customers?.length || !formData || !formData.customerID) return;
    const currentIndex = customers.findIndex(
      (cust) => cust.customerID === formData.customerID
    );
    if (currentIndex === -1) return;
    const prevIndex = (currentIndex - 1 + customers.length) % customers.length;
    dispatch(setSelectedCustomer(customers[prevIndex]));
    clearSelected();
  }, [customers, formData, dispatch, clearSelected]);

  const handleNext = useCallback(() => {
    if (!customers?.length || !formData || !formData.customerID) return;
    const currentIndex = customers.findIndex(
      (cust) => cust.customerID === formData.customerID
    );
    if (currentIndex === -1) return;
    const nextIndex = (currentIndex + 1) % customers.length;
    dispatch(setSelectedCustomer(customers[nextIndex]));
    clearSelected();
  }, [customers, formData, dispatch, clearSelected]);

  const [contactForm, setContactForm] = useState({});
  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };
  const [showNewContactForm, setShowNewContactForm] = useState(false);

  const [newEntityMode, setNewEntityMode] = useState({
    active: false,
    type: null,
  });
  const handleOpenNew = (entityType) => {
    if (entityType === "contact") {
      dispatch(clearSelectedCustomerContact());
    }
    setNewEntityMode({ active: true, type: entityType });
    setIsInfoOpen(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setValidationErrors({});
  };

  useEffect(() => {
    if (open) {
      setValidationErrors({});
    }
  }, [open, setValidationErrors]);

  // Guard: If formData is empty, show a loading indicator inside the Dialog.
  if (!formData || Object.keys(formData).length === 0) {
    return (
      <Dialog open={open} onClose={handleClose}>
        <DialogContent
          style={{
            minHeight: "500px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <LoadingCircle />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          style: {
            position: "absolute",
            top: navHeight,
            left: "50%",
            transform: "translateX(-50%)",
            margin: 4,
            transition: "width 0.3s ease, height 0.3s ease",
            width: isExpanded ? "90%" : "1000px",
            height: `calc(100vh - ${navHeight + 8}px)`,
            overflow: "auto",
          },
        }}
        style={{ zIndex: 1400 }}
      >
        <ModalHeader
          isExpanded={isExpanded}
          setIsExpanded={() => setIsExpanded((prev) => !prev)}
          handleCloseModal={handleClose}
          isEditMode={isEditMode}
          handleEditButtonClick={handleToggleEdit}
          handleCancel={handleCancelEdit}
          handleDelete={handleDelete}
          isSaving={isSubmitting || autosaveStatus === "Saving..."}
          autosaveStatus={autosaveStatus}
          handleOpenAddModal={handleOpenAddFromView}
        />
        <div style={{ width: "90%", margin: "0 auto" }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-3xl mb-4">Customer</h2>
            <button
              onClick={handleInfoClick}
              className={`p-2 bg-white hover:bg-gray-200 transition ${isInfoOpen ? "text-sss-info" : "text-gray-800"
                }`}
            >
              {isInfoOpen ? <InfoIcon /> : <InfoOutlinedIcon />}
            </button>
          </div>
          <hr />
        </div>
        <DialogContent
          style={{
            minHeight: "500px",
            width: "90%",
            margin: "0 auto",
            padding: 0,
            height: "100%",
          }}
          sx={{ p: 0, pr: 2, pt: 2 }}
        >
          <div className="flex h-full transition-all duration-300">
            <div
              className={`transition-all duration-300 ${isInfoOpen ? "w-5/12" : "w-full"
                } h-full overflow-y-auto`}
            >
              {validationErrors.form && (
                <div className="text-red-500 mb-4">{validationErrors.form}</div>
              )}
              <ModalAccordion label="General" defaultExpanded>
                <div className="flex flex-col md:flex-row gap-4 w-full">
                  <RenderFieldsCustomers
                    fields={customerGeneralInfo}
                    formData={formData}
                    handleChange={handleFieldChange}
                    readOnly={!isEditMode}
                    validationErrors={validationErrors}
                    dropdownLists={dropdownLists}
                  />
                </div>
                <div className="flex flex-col md:flex-row gap-4 w-full py-2">
                  <RenderFieldsCustomers
                    fields={customerComment}
                    formData={formData}
                    handleChange={handleFieldChange}
                    readOnly={!isEditMode}
                    validationErrors={validationErrors}
                  />
                </div>
              </ModalAccordion>
              <ModalAccordion label="Address" defaultExpanded>
                <div className="flex flex-col md:flex-row gap-4 w-full">
                  <RenderAddressFields
                    fields={customerAddressInfo}
                    formData={formData}
                    handleChange={handleFieldChange}
                    readOnly={!isEditMode}
                    validationErrors={validationErrors}
                  />
                </div>
              </ModalAccordion>
              {/* <ModalAccordion label="Additional" defaultExpanded>
                <div className="flex flex-col md:flex-row gap-4 w-full">
                  <RenderFields
                    fields={customerAdditionalInfo}
                    formData={formData}
                    handleChange={handleFieldChange}
                    readOnly={!isEditMode}
                    validationErrors={validationErrors}
                  />
                </div>
              </ModalAccordion> */}
              <ModalAccordion label="Contacts" defaultExpanded>
                <CustomerContactTable
                  contactForm={contactForm}
                  handleContactChange={handleContactChange}
                  isEditMode={isEditMode}
                  onAddNew={() => handleOpenNew("contact")}
                  setShowNewContactForm={setShowNewContactForm}
                />
              </ModalAccordion>
              <ModalAccordion label="Buildings" defaultExpanded>
                {!formData?.customerID && <LoadingCircle />}
                <CustomerBuildingsTable
                  customerID={formData?.customerID}
                  isEditMode={isEditMode}
                  onAddNew={() => handleOpenNew("building")}
                  newBuildingId={newBuildingId}
                  setNewBuildingId={setNewBuildingId}
                />
              </ModalAccordion>

              <ModalAccordion label="Customer Account" defaultExpanded>

                <CreateAccount customerId={formData?.customerID} userId={formData?.userId} userName={formData?.userName} setFormData={setFormData} updateList={updateList} />

              </ModalAccordion>



            </div>
            <div
              className={`transition-all duration-300 overflow-hidden ${isInfoOpen ? "w-7/12 opacity-100" : "w-0 opacity-0"
                } h-full`}
            >
              <div className="h-full overflow-y-auto mt-4 ml-4">
                <div className="p-4">
                  {/* Additional information display can be added here */}
                  <InformationDisplay
                    isEditMode={isEditMode}
                    newMode={newEntityMode.active}
                    newEntityType={newEntityMode.type} // either "contact" or "building"
                    onCloseNewMode={() =>
                      setNewEntityMode({ active: false, type: null })
                    }
                    updateList={updateList}
                  />
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div style={{ width: "90%", margin: "0 auto" }}>
          <hr />
        </div>
        <DialogActions
          style={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "#fff",
            zIndex: 1500,
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={handlePrev}
            className="p-2 bg-white hover:bg-gray-200 transition"
          >
            <ArrowBackIcon />
          </button>
          <button
            onClick={handleNext}
            className="p-2 mr-2 bg-white hover:bg-gray-200 transition"
          >
            <ArrowForwardIcon />
          </button>
        </DialogActions>
      </Dialog>
      <AlertSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        autoHideDuration={3000}
      />
    </>
  );
};

CustomerViewModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  updateList: PropTypes.func,
  handleOpenAddFromView: PropTypes.func,
};

export default CustomerViewModal;
